'use client';

import { ComponentIntroHero, Enum_Componentlinkslink_Type } from "@/app/lib/generated/graphql-types";
import { notNull } from "@/app/lib/common/not-null";
import Link from "next/link";
import EditableTypographyV2 from "@/app/features/shared/editable-typography/EditableTypographyV2";
import EditableMarkdownV2 from "@/app/features/shared/editable-markdown/EditableMarkdownV2";
import sharedStyles from "../shared/hero-intro-shared.module.scss";
import editableStyles from "./hero-intro-editable.module.scss";
import {
    DefaultHeroIntroContentRootFrame,
    DefaultHeroIntroDescriptionFrame,
    DefaultHeroIntroHeadingFrame,
    DefaultHeroIntroLinksFrame
} from "@/app/features/shared/intros/hero/ui/shared/DefaultHeroIntroContentFrames";
import { MediaRef } from "@/app/features/shared/__to_be_integrated/types/general/media/media";
import { useState } from "react";
import clsx from "clsx";

type DefaultHeroIntroContentEditableProps = {
    data: Omit<ComponentIntroHero, 'image'> & { image: MediaRef } ;
    onTitleChange: (title: string) => void;
    onSubtitleChange: (subtitle: string) => void;
    onDescriptionChange: (description: string | undefined) => void;
    errorForField?: (field: string) => string | null;
};

export default function DefaultHeroIntroContentEditable({
    data,
    onTitleChange,
    onSubtitleChange,
    onDescriptionChange,
    errorForField
}: DefaultHeroIntroContentEditableProps) {
    const [isMarkdownEditing, setIsMarkdownEditing] = useState(false);
    const links = (data.links || []).filter(notNull);

    const titleError = errorForField?.('intro.title') || undefined;
    const subtitleError = errorForField?.('intro.subtitle') || undefined;
    const descriptionError = errorForField?.('intro.description') || undefined;

    return (
        <DefaultHeroIntroContentRootFrame
            stackClassName={clsx(
                sharedStyles.defaultHeroContentStack,
                isMarkdownEditing && sharedStyles.defaultHeroContentStackEditing
            )}
            disableScrollable={isMarkdownEditing}
            scrollableChildren={(
                <>
                    <DefaultHeroIntroHeadingFrame>
                        <EditableTypographyV2
                            label="Titel"
                            variant="h2"
                            component="h1"
                            color="primary"
                            className={sharedStyles.heroTitle}
                            value={data.title}
                            onChange={onTitleChange}
                            error={Boolean(titleError)}
                            helperText={titleError}
                            slots={{
                                positioner: {
                                    className: clsx(
                                        editableStyles.editableFieldPositioner,
                                        editableStyles.editableFieldPositionerTopInset
                                    )
                                },
                                root: { className: editableStyles.editableFieldRoot },
                            }}
                        />
                        <EditableTypographyV2
                            label="Untertitel"
                            variant="subtitle1"
                            component="h2"
                            color="text.secondary"
                            className={sharedStyles.heroSubtitle}
                            value={data.subtitle || ''}
                            onChange={onSubtitleChange}
                            error={Boolean(subtitleError)}
                            helperText={subtitleError}
                            slots={{
                                positioner: { className: editableStyles.editableFieldPositioner },
                                root: { className: editableStyles.editableFieldRoot },
                            }}
                        />
                    </DefaultHeroIntroHeadingFrame>
                    <DefaultHeroIntroDescriptionFrame>
                        <EditableMarkdownV2
                            label="Beschreibung"
                            value={data.description || ''}
                            onChange={onDescriptionChange}
                            onEditingChange={setIsMarkdownEditing}
                            typographyOverrides={{ paragraph: "body2" }}
                            error={Boolean(descriptionError)}
                            helperText={descriptionError}
                            slots={{
                                badge: { className: editableStyles.editableMarkdownBadge },
                                root: { className: editableStyles.editableMarkdownRoot },
                            }}
                        />
                    </DefaultHeroIntroDescriptionFrame>
                </>
            )}
            bottomChildren={links.length > 0 && (
                <DefaultHeroIntroLinksFrame>
                    {links.map((link) => (
                        <Link
                            key={link.id}
                            href={link.url}
                            target={link.type === Enum_Componentlinkslink_Type.External ? "_blank" : "_self"}
                            rel={link.type === Enum_Componentlinkslink_Type.External ? "noopener noreferrer" : undefined}
                            prefetch={false}
                            className={sharedStyles.heroLink}
                        >
                            {link.name || link.url}
                        </Link>
                    ))}
                </DefaultHeroIntroLinksFrame>
            )}
        />
    );
}
